import React, { useState, useContext, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SiteContext } from "../context/site";
import { petKindLabel } from "../utils/petCategories";
import { useCachedData } from "../hooks/useCachedData";

const ConsultationPetSelection = React.memo(({ animals, setAnimal, selectedPet }) => {
  const navigate = useNavigate();
  const { 
    base_url,
    especes,
    getAContent,
    speciesBreedList,
    photoAnimalDefaultSrc,
  } = useContext(SiteContext);
  
  const { fetchWithCache } = useCachedData();
  const [imageErrors, setImageErrors] = useState({});
  const [breedNames, setBreedNames] = useState({});
  const [isLoadingBreeds, setIsLoadingBreeds] = useState(false);
  const loadedSpeciesRef = useRef(new Set());
  const photoDefaultSrc = photoAnimalDefaultSrc;

  const formatShortDate = useCallback((dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }, []);

  const getImageSrc = useCallback((pet) => {
    if (imageErrors[pet.id]) return photoDefaultSrc;
    if (pet.picture) return `${base_url}uploads/files/pets/${pet.picture}`;
    return photoDefaultSrc;
  }, [base_url, imageErrors]);

  const handleImageError = useCallback((petId) => {
    setImageErrors(prev => ({ ...prev, [petId]: true }));
  }, []);

  const handlePetClick = useCallback((pet) => {
    setAnimal(pet);
  }, [setAnimal]);

  // Load breed names efficiently with caching
  useEffect(() => {
    if (!animals?.length) return;

    const loadBreeds = async () => {
      const speciesToLoad = [];
      const breedMap = {};

      for (const pet of animals) {
        const speciesId = pet.espece?.id;
        const breedId = pet.race?.id;
        
        if (speciesId && breedId && !loadedSpeciesRef.current.has(speciesId)) {
          speciesToLoad.push(speciesId);
        }
        
        if (pet.race?.nom) {
          breedMap[breedId] = { nom: pet.race.nom, tagRef: pet.race.tagRef || null };
        }
      }

      const needsFetching = speciesToLoad.length > 0 && 
        animals.some(pet => pet.race?.id && (!pet.race?.nom || !pet.race?.tagRef));
      
      if (!needsFetching && Object.keys(breedMap).length === 0) {
        return;
      }

      setIsLoadingBreeds(true);

      try {
        for (const speciesId of speciesToLoad) {
          if (!loadedSpeciesRef.current.has(speciesId)) {
            try {
              const breeds = await fetchWithCache(
                `breeds_${speciesId}`,
                () => speciesBreedList(speciesId),
                300000 
              );
              
              if (breeds && Array.isArray(breeds)) {
                breeds.forEach(breed => {
                  if (breed.id && breed.nom) {
                    breedMap[breed.id] = { nom: breed.nom, tagRef: breed.tagRef || null };
                  }
                });
              }
              loadedSpeciesRef.current.add(speciesId);
            } catch (error) {
              console.error(`Error loading breeds for species ${speciesId}:`, error);
            }
          }
        }
        
        setBreedNames(breedMap);
      } finally {
        setIsLoadingBreeds(false);
      }
    };

    loadBreeds();
  }, [animals, speciesBreedList, fetchWithCache]);

  const memoizedAnimals = useMemo(() => {
    if (!animals?.length) return [];
    return animals;
  }, [animals]);

  if (!memoizedAnimals.length) {
    return (
      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <button
          className="consultation-next-button"
          onClick={() => navigate("/my-pets")}
        >
          {getAContent('cmp_vetonest.com_CreateNewPet_Btn')}
        </button>
      </div>
    );
  }

  return (
    <div>
      {isLoadingBreeds && (
        <div style={{ textAlign: "center", padding: "10px" }}>
          <span style={{ fontSize: "12px", color: "#888" }}>
            {getAContent('cmp_vetonest.com_LoadingBreeds_Txt')}
          </span>
        </div>
      )}
      <div className="consultation-pet-cards-container"> 
        {memoizedAnimals.map((pet) => {
          const _breedEntry = pet.race?.id ? breedNames[pet.race.id] : null;
          const breedName = _breedEntry
            ? (_breedEntry.tagRef ? getAContent(_breedEntry.tagRef) : (_breedEntry.nom || '—'))
            : (pet.race?.nom || '—');
          
          return (
            <div
              key={pet.id}
              className={`consultation-pet-card ${selectedPet?.id === pet.id ? 'selected' : ''}`}
              onClick={() => handlePetClick(pet)}
            >
              <div className="consultation-pet-image-container">
                <img
                  src={getImageSrc(pet)}
                  alt={pet.nom}
                  className="consultation-pet-card-img"
                  loading="lazy"
                  onError={() => handleImageError(pet.id)}
                />
              </div>
              <div className="consultation-pet-details">
                <h4 className="consultation-pet-name">{pet.nom}</h4>
                <p className="consultation-pet-info">
                  {getAContent('cmp_vetonest.com_Species_Label')} : {petKindLabel(getAContent, pet, especes) || getAContent('cmp_vetonest.com_Unknown_Txt')}
                  <br />
                  {getAContent('cmp_vetonest.com_Breed_Label')} : {breedName}
                  <br />
                  <span className="date-line">
                    {getAContent('cmp_vetonest.com_BirthDate_Label')} : {formatShortDate(pet.dateNaissance?.date || pet.dateNaissance)}
                  </span>
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

ConsultationPetSelection.displayName = 'ConsultationPetSelection';

export default ConsultationPetSelection;